(ns dashboard.entity.analysis.applied-db
  (:require [clojure.string :as str]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.entity.analysis.profile :refer [format-analysis-id]]
            [dashboard.notifications :as notifications]
            [dashboard.services.http-config :as http-config]
            [dashboard.ui :as ui]
            [io.repliance.schemas.structured-analysis :as structured-analysis-schemas]
            [io.repliance.util.tree :as tree]
            [malli.core :as m]
            [malli.error :as me]
            [malli.util :as mu]
            [re-frame.core :as rf]))

;; ----------------------------------------------------------------------------
;;  Subscriptions
;; ----------------------------------------------------------------------------

(rf/reg-sub
 ::editor
 (fn [db _]
   (:applied-structured-analysis-editor db)))

(rf/reg-sub
 ::editor-saving
 :<- [::editor]
 (fn [editor _]
   (:saving editor)))

(defn editor-saving*
  []
  (rf/subscribe [::editor-saving]))

(rf/reg-sub
 ::editor-dependencies-to-skip
 :<- [::editor]
 (fn [editor _]
   (:dependencies-to-skip editor #{})))

(defn editor-dependencies-to-skip*
  []
  (rf/subscribe [::editor-dependencies-to-skip]))

(defn find-family-state-ids
  "Find the descendant state ids of root state-id, including root itself"
  [root-state-id all-analyses]
  (letfn [(helper [result parents]
            (let [children (mapcat (fn [parent-state-id]
                                     (keep (fn [analysis]
                                             (get (:state-dependencies analysis) parent-state-id))
                                           all-analyses))
                                   parents)]
              (if (seq children)
                (helper (into result children) children)
                result)))]
    (set (helper [root-state-id] [root-state-id]))))

;; TODO: Check if we need to have specific versions of structured analyses to walk through.
;;       Now we walk through the latest versions only
(rf/reg-sub
 ::state-dependent-analyses
 (fn [db [_ state-id]]
   (when state-id
     (let [all-latest-structured-analyses (map (comp first second) (get-in db [:structured-analyses :data]))
           analyses-sorted-hierarchically (tree/sort-hierarchical all-latest-structured-analyses
                                                                  :construct-id
                                                                  compare
                                                                  :construct-id)
           family (find-family-state-ids state-id analyses-sorted-hierarchically)
           analyses-family (keep (fn [{:keys [state-dependencies] :as analysis}]
                                   (when-let [dependent-state-id (some family (keys state-dependencies))]
                                     (assoc analysis :dependent-state-id (state-dependencies dependent-state-id))))
                                 analyses-sorted-hierarchically)]
       (seq analyses-family)))))

(defn state-dependent-analyses*
  [state-id]
  (rf/subscribe [::state-dependent-analyses state-id]))

(rf/reg-sub
 ::latest-structured-analysis
 (fn [db [_ analysis-id]]
   (-> (get-in db [:structured-analyses :data analysis-id])
       first)))

(defn latest-structured-analysis*
  [analysis-id]
  (rf/subscribe [::latest-structured-analysis analysis-id]))

(rf/reg-sub
 ::latest-applied-structured-analysis
 (fn [db [_ analysis-id]]
   (-> (get-in db [:applied-analyses :data analysis-id])
       first)))

(defn latest-applied-structured-analysis*
  [analysis-id]
  (rf/subscribe [::latest-applied-structured-analysis analysis-id]))

(rf/reg-sub
 ::all-applied-analysis-versions
 (fn [db [_ analysis-id]]
   (get-in db [:applied-analyses :data analysis-id])))

(defn all-applied-analysis-versions*
  [analysis-id]
  (rf/subscribe [::all-applied-analysis-versions analysis-id]))

(rf/reg-sub
 ::linked-answers
 (fn [db [_ construct-id]]
   {:answers (get-in db [:applied-analysis-construct->answers :data construct-id])
    :loading (get-in db [:applied-analysis-construct->answers :loading])}))

(defn linked-answers*
  [construct-id]
  (rf/subscribe [::linked-answers construct-id]))

(rf/reg-sub
 ::linked-evidence
 (fn [db [_ applied-structured-analysis-id]]
   {:evidence (get-in db [:applied-analysis-construct->evidence :data applied-structured-analysis-id])
    :loading (get-in db [:applied-analysis-construct->evidence :loading])}))

(defn linked-evidence*
  [applied-structured-analysis-id]
  (rf/subscribe [::linked-evidence applied-structured-analysis-id]))

(rf/reg-sub
 ::last-references-by-date
 (fn [db _]
   (mapv :reference (get-in db [:last-references-by-date :data]))))

(defn last-references-by-date*
  []
  (rf/subscribe [::last-references-by-date]))

;; ----------------------------------------------------------------------------
;;  Utility Functions
;; ----------------------------------------------------------------------------

(def empty-state-evidence
  {:provider "Client"
   :evidence-type "analysis"
   :information-type nil
   :reference nil
   :comment nil
   :document-id nil
   :validated-by-automation nil
   :expiry-date nil
   :expiry-action nil
   :control-status "Not Applicable"
   :responsible-org nil
   :responsible-team nil
   :responsible-person-id nil})

;; TODO - this function assumed no ASA for the SA and should be removed... :)
(defn make-empty-editor-state [{:keys [construct-id
                                       construct-version
                                       categories] :as _structured-analysis}]
  (let [category-names (set (map :name categories))]
    {:state-id nil
     :construct-id construct-id
     :construct-version construct-version
     :state-evidence (if (contains? category-names "Policy")
                                   [{:provider "Client"
                                     :information "Policy Document"
                                     :reference nil
                                     :comment nil
                                     :document-id nil
                                     :automation nil}]
                                   [empty-state-evidence])
     :product nil
     :answer-ids #{}}))

(defn validate-humanize [data schema]
  (-> schema
      (m/explain data)
      (me/humanize {:errors me/default-errors})))

(defn set-field [{:keys [validation-run]} set-editor-state ks value]
  (let [value (if (str/blank? value)
                nil
                value)
        validation-errors (when validation-run
                            (validate-humanize value (mu/get-in structured-analysis-schemas/AppliedStructuredAnalysisData ks)))]
    (set-editor-state #(-> %
                           (update :state assoc-in ks value)
                           (assoc  :validation-errors validation-errors)))))

(defn set-evidence-field [{:keys [validation-run]} set-editor-state ks value]
  (let [value (if (str/blank? value)
                nil
                value)
        validation-errors (when validation-run
                            (validate-humanize value (mu/get-in structured-analysis-schemas/NewAnalysisEvidence ks)))]
    (set-editor-state #(-> %
                           (update :state-evidence assoc-in ks value)
                           (assoc  :validation-errors validation-errors)))))

(defn make-analyses-to-create [root state-dependent-analyses]
  (into [root]
        (map (fn [dependent]
               {:construct-id (:construct-id dependent)
                :construct-version (:construct-version dependent)
                :state-id (:dependent-state-id dependent)
                :product (:product root)
                :state-set-by "dependency"
                :answer-ids #{}}))
        state-dependent-analyses))

(defn validate [state]
  (validate-humanize state structured-analysis-schemas/AppliedStructuredAnalysisData))

;; ----------------------------------------------------------------------------
;;  Events and Effects
;; ----------------------------------------------------------------------------

(rf/reg-event-fx
 ::create-success
 (fn [{:keys [db]} [_ applied-structured-analyses]]
   (analysis.db/get-structured-analyses false)
   (analysis.db/get-applied-analyses false)
   {:db (assoc-in db [:applied-structured-analysis-editor :saving] false)
    :fx [[:dispatch [::notifications/add {:message (str "Created "
                                                        (if (= 1 (count applied-structured-analyses))
                                                          "analysis "
                                                          "analyses: ")
                                                        (str/join ", " (map #(format-analysis-id (:construct-id %)) applied-structured-analyses)))
                                          :class "is-success"}]]
          ;; If the state dependancies modal is open, close it
          ;; the [::close-editor :edit] destroys the last editor modal's state!
         (when (> (count applied-structured-analyses) 1)
           [:dispatch [::close-editor]])
         [:dispatch [::close-editor :edit]]
         [:dispatch [::get-linked-evidence (-> applied-structured-analyses
                                               first
                                               :applied-structured-analysis-id)]]
         [:dispatch [::get-last-references-by-date true]]]}))

(rf/reg-event-fx
 ::create-failure
 (fn [{:keys [db]} [_ analyses-to-create]]
   {:db (assoc-in db [:applied-structured-analysis-editor :saving] false)
    :dispatch [::ui/set-error (str "Failed to create "
                                   (if (= 1 (count analyses-to-create))
                                     "analysis "
                                     "analyses: ")
                                   (str/join ", " (map #(format-analysis-id (:construct-id %)) analyses-to-create)))]}))


(rf/reg-event-fx
  ::create
  (fn [{:keys [db]} [_ applied-structured-analyses state-evidence]]
    {:db (assoc-in db [:applied-structured-analysis-editor :saving] true)
     :http-xhrio {:method :post
                  :uri "/api/applied-structured-analysis/"
                  :params {:analyses (remove empty? applied-structured-analyses)
                           :state-evidence (or state-evidence [])}
                  :format http-config/transit-request-format
                  :response-format http-config/transit-response-format
                  :on-success [::create-success]
                  :on-failure [::create-failure applied-structured-analyses]}}))

(defn create
  [applied-structured-analyses state-evidence]
  (rf/dispatch [::create applied-structured-analyses state-evidence]))

;; ----------

(rf/reg-event-fx
  ::create-applied-structured-analysis
  (fn [{:keys [db]} [_ applied-structured-analysis state-evidence state-dependent-analyses confirmation-modal]]
    {:fx (cond
           (and state-dependent-analyses
                (not (get-in db [:applied-structured-analysis-editor :confirmation-shown])))
           [[:dispatch [::ui/push-modal
                        {:component confirmation-modal
                         :ctx {:applied-structured-analysis applied-structured-analysis
                               :state-evidence state-evidence
                               :state-dependent-analyses state-dependent-analyses}
                         :content-props {:style {:width "50vw"
                                                 :max-height "90vh"
                                                 :padding 0}}}]]]

           :else
           [[:dispatch [::create [applied-structured-analysis] state-evidence]]])}))

(defn create-applied-structured-analysis
  [applied-structured-analysis state-evidence state-dependent-analyses confirmation-modal]
  (rf/dispatch [::create-applied-structured-analysis applied-structured-analysis state-evidence state-dependent-analyses confirmation-modal]))

;; ----------

(rf/reg-event-fx
  ::close-editor
  (fn [{:keys [db]} [_ mode]]
    {:db (if (= :edit mode)
           (-> db
               (assoc-in [:applied-structured-analysis-editor :dependencies-to-skip] nil)
               (assoc-in [:applied-structured-analysis-editor :confirmation-shown] nil))
           db)
     :dispatch [::ui/pop-modal]}))

(defn close-editor
  [mode]
  (rf/dispatch [::close-editor mode]))

;; ----------

(rf/reg-event-db
 ::get-linked-answers-success
 (fn [db [_ construct-id answers]]
   (-> db
       (assoc-in [:applied-analysis-construct->answers :loading] false)
       (assoc-in [:applied-analysis-construct->answers :data construct-id] answers))))

(rf/reg-event-fx
  ::get-linked-answers
  (fn [{:keys [db]} [_ construct-id]]
    (when construct-id
      {:db (assoc-in db [:applied-analysis-construct->answers :loading] true)
       :http-xhrio {:method :get
                    :uri "/api/applied-structured-analysis/answers"
                    :url-params {:construct-id construct-id}
                    :format http-config/transit-request-format
                    :response-format http-config/transit-response-format
                    :on-success [::get-linked-answers-success construct-id]
                    :on-failure [::ui/set-error "Failed to get associated answers"]}})))

(defn get-linked-answers
  [construct-id]
  (rf/dispatch [::get-linked-answers construct-id]))

;; ----------

(rf/reg-event-db
  ::skip-dependency
  (fn [db [_ id skip?]]
    (update-in db [:applied-structured-analysis-editor :dependencies-to-skip]
               (fnil #(if skip?
                        (conj % id)
                        (disj % id))
                     #{}))))

(defn skip-dependency
  [construct-id skip?]
  (rf/dispatch [::skip-dependency construct-id skip?]))

;; ----------

(rf/reg-event-db
 ::get-linked-evidence-success
 (fn [db [_ applied-structured-analysis-id evidence]]
   (-> db
       (assoc-in [:applied-analysis-construct->evidence :loading] false)
       (assoc-in [:applied-analysis-construct->evidence :data applied-structured-analysis-id] evidence))))

(rf/reg-event-fx
 ::get-linked-evidence
 (fn [{:keys [db]} [_ applied-structured-analysis-id]]
   (when applied-structured-analysis-id
     {:db (assoc-in db [:applied-analysis-construct->evidence :loading] true)
      :http-xhrio {:method :get
                   :uri "/api/applied-structured-analysis/evidence"
                   :url-params {:applied-structured-analysis-id applied-structured-analysis-id}
                   :format http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success [::get-linked-evidence-success applied-structured-analysis-id]
                   :on-failure [::ui/set-error "Failed to get associated evidence"]}})))

(defn get-linked-evidence
  [applied-structured-analysis-id]
  (rf/dispatch [::get-linked-evidence applied-structured-analysis-id]))

;; ----------

(rf/reg-event-db
 ::get-last-references-by-date-success
 (fn [db [_ data]]
   (-> db
       (assoc-in [:last-references-by-date :loading] false)
       (assoc-in [:last-references-by-date :data] data))))

(rf/reg-event-fx
 ::get-last-references-by-date
 (fn [{:keys [db]} [_ always-get]]
   (when (or always-get (nil? (get-in db [:last-references-by-date :data])))
     {:db (assoc-in db [:last-references-by-date :loading] true)
      :http-xhrio {:method :get
                   :uri "/api/applied-structured-analysis/last-references-by-date"
                   :format http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success [::get-last-references-by-date-success]
                   :on-failure [::ui/set-error "Failed to get latest references"]}})))

(defn get-last-references-by-date
  [always-get]
  (rf/dispatch [::get-last-references-by-date always-get]))

;; ----------